// src/pages/monitoring/styles.js
import { levelStyles, clamp } from "./utils";

export function buildStyles({ selected }) {
  return {
    topBar: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      marginBottom: 14,
      flexWrap: "wrap", // ✅ verhindert "quetschen" auf schmalen Screens
    },

    titleWrap: { display: "flex", flexDirection: "column", gap: 6 },
    title: { margin: 0, fontSize: 22, letterSpacing: 0.2 },
    subtitle: { margin: 0, fontSize: 12, opacity: 0.75 },

    headerRow: {
      display: "flex",
      gap: 10,
      alignItems: "center",
      marginTop: 4,
      flexWrap: "wrap",
    },

    tabRow: {
      display: "inline-flex",
      gap: 6,
      padding: 4,
      borderRadius: 12,
      background: "#eef0f3",
      border: "1px solid var(--border)",
    },

    tab: (active) => ({
      height: 34,
      padding: "0 12px",
      borderRadius: 10,
      border: "none",
      background: active ? "#ffffff" : "transparent",
      boxShadow: active ? "0 1px 2px rgba(16,24,40,0.12)" : "none",
      color: active ? "var(--text)" : "var(--muted)",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: 13,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
    }),

    pill: (status) => {
      const ok = status === "ok";
      const warn = status === "warn";

      const bg = ok
        ? "rgba(22,163,74,0.10)"
        : warn
        ? "rgba(217,119,6,0.12)"
        : "rgba(220,38,38,0.10)";
      const border = ok
        ? "rgba(22,163,74,0.22)"
        : warn
        ? "rgba(217,119,6,0.22)"
        : "rgba(220,38,38,0.22)";
      const fg = ok ? "#16a34a" : warn ? "#b45309" : "#dc2626";
      const dot = ok ? "#16a34a" : warn ? "#b45309" : "#dc2626";
      return {
        dot,
        style: {
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          borderRadius: 999,
          background: bg,
          border: `1px solid ${border}`,
          color: fg,
          fontSize: 12,
          whiteSpace: "nowrap",
        },
      };
    },

    // ✅ NEW: Refresh controls (oben rechts) — professionell & aligned
    refreshWrap: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 12,
    },

    refreshCluster: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "6px 12px",
      borderRadius: 10,
      background: "#ffffff",
      border: "1px solid var(--border)",
    },

    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      opacity: 0.9,
      userSelect: "none",
      whiteSpace: "nowrap",
      lineHeight: 1,
    },

    checkbox: {
      width: 14,
      height: 14,
      margin: 0,
    },

    intervalGroup: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },

    intervalLabel: {
      fontSize: 12,
      opacity: 0.8,
      whiteSpace: "nowrap",
      lineHeight: 1,
    },

    intervalInput: {
      height: 36, // ✅ gleiche Höhe wie button/input
      width: 76,
      borderRadius: 10,
      border: "1px solid var(--border-strong)",
      backgroundColor: "#ffffff",
      color: "var(--text)",
      padding: "0 10px",
      outline: "none",
      textAlign: "center",
    },

    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: 12,
      marginBottom: 12,
    },

    perfGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
      gap: 12,
      marginBottom: 12,
    },

    perfRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: 12,
      marginBottom: 12,
    },

    card: {
      background: "var(--card, #ffffff)",
      border: "1px solid rgba(15,23,42,0.088)",
      borderRadius: 14,
      padding: 12,
      boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
    },

    kpiRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
    },
    kpiLabel: { fontSize: 12, opacity: 0.75 },
    kpiValue: { fontSize: 22, fontWeight: 700, letterSpacing: 0.2 },

    controls: {
      display: "grid",
      gridTemplateColumns: "1.2fr 1fr 1fr 1.4fr auto",
      gap: 10,
      alignItems: "end",
    },
    label: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      fontSize: 12,
      opacity: 0.85,
    },

    input: {
      height: 36,
      borderRadius: 10,
      border: "1px solid var(--border-strong)",
      backgroundColor: "#ffffff",
      color: "var(--text)",
      padding: "0 10px",
      outline: "none",
    },

    button: (primary) => ({
      height: 36,
      padding: "0 12px",
      borderRadius: 10,
      border: primary ? "1px solid transparent" : "1px solid var(--border)",
      background: primary ? "var(--brand)" : "#ffffff",
      color: primary ? "#ffffff" : "var(--text)",
      cursor: "pointer",
      fontWeight: 600,
      whiteSpace: "nowrap",
    }),

    // Logs table
    split: {
      display: "grid",
      gridTemplateColumns: selected ? "1fr 420px" : "1fr",
      gap: 12,
      alignItems: "start",
    },

    tableCard: {
      background: "var(--card, #ffffff)",
      border: "1px solid rgba(15,23,42,0.088)",
      borderRadius: 14,
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
    },

    tableHeader: {
      position: "sticky",
      top: 0,
      zIndex: 2,
      display: "grid",
      gridTemplateColumns: "180px 92px 1fr 86px",
      gap: 10,
      padding: "10px 12px",
      borderBottom: "1px solid rgba(15,23,42,0.088)",
      fontSize: 12,
      opacity: 0.85,
      background: "#f8f9fb",
      backdropFilter: "blur(6px)",
    },

    list: {
      maxHeight: "min(64vh, 680px)",
      overflow: "auto",
    },

    row: (active) => ({
      display: "grid",
      gridTemplateColumns: "180px 92px 1fr 86px",
      gap: 10,
      padding: "10px 12px",
      borderBottom: "1px solid rgba(15,23,42,0.066)",
      alignItems: "center",
      cursor: "pointer",
      background: active ? "rgba(0,108,253,0.1)" : "transparent",
    }),

    time: { fontSize: 12, opacity: 0.85 },

    msg: {
      fontSize: 12,
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      opacity: 0.95,
    },

    badge: (lvl) => {
      const s = levelStyles(lvl);
      return {
        justifySelf: "start",
        display: "inline-flex",
        alignItems: "center",
        height: 22,
        padding: "0 8px",
        borderRadius: 999,
        background: s.bg,
        border: `1px solid ${s.border}`,
        color: s.fg,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
      };
    },

    smallBtn: {
      justifySelf: "end",
      height: 28,
      padding: "0 10px",
      borderRadius: 10,
      border: "1px solid var(--border)",
      background: "#ffffff",
      color: "var(--text)",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: 12,
    },

    side: {
      position: "sticky",
      top: 16,
      background: "#ffffff",
      border: "1px solid rgba(15,23,42,0.088)",
      borderRadius: 14,
      padding: 12,
      boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
    },

    sideTitle: { margin: 0, fontSize: 14, fontWeight: 800 },

    sideMeta: {
      marginTop: 8,
      fontSize: 12,
      opacity: 0.8,
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
    },

    pre: {
      marginTop: 10,
      padding: 10,
      borderRadius: 12,
      border: "1px solid rgba(15,23,42,0.088)",
      background: "#f8f9fb",
      fontSize: 12,
      lineHeight: 1.45,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    },

    errorBox: {
      marginBottom: 12,
      borderRadius: 14,
      padding: 12,
      background: "rgba(220,38,38,0.10)",
      border: "1px solid rgba(220,38,38,0.18)",
      color: "#b91c1c",
      whiteSpace: "pre-wrap",
      fontSize: 12,
    },

    empty: { padding: 16, fontSize: 12, opacity: 0.8 },

    footerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 10,
      padding: 12,
      background: "#f8f9fb",
      borderTop: "1px solid rgba(15,23,42,0.066)",
    },

    hint: { fontSize: 12, opacity: 0.75, marginTop: 6 },

    barWrap: {
      marginTop: 8,
      height: 10,
      borderRadius: 999,
      background: "rgba(15,23,42,0.088)",
      overflow: "hidden",
      border: "1px solid rgba(15,23,42,0.11)",
    },

    bar: (pct, color) => ({
      width: `${clamp(pct, 0, 100)}%`,
      height: "100%",
      background: color,
    }),
  };
}
